import { useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  DetailRow,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { ContributeTaxonForm } from "~/features/plants/ContributeTaxonForm";
import { TaxonSearchField } from "~/features/plants/TaxonSearchField";
import { RANK_LABELS, useTaxonLineage } from "~/state/taxonomy";
import type { TaxonSearchResult } from "~/state/taxonomy";
import { spacing } from "~/styles/theme";

export default function DiscoverScreen() {
  const [selected, setSelected] = useState<TaxonSearchResult | null>(null);
  const [contributingTerm, setContributingTerm] = useState<string | null>(null);
  const lineage = useTaxonLineage(selected?.id);

  return (
    <ProtectedScreen
      title="Botanical index"
      subtitle="A shared database of proper botanical names, cultivars, and the common names people actually use."
    >
      <Card gap="md">
        <SectionHeader title="Search" />
        <TaxonSearchField
          selected={selected}
          onSelect={(taxon) => {
            setSelected(taxon);
            setContributingTerm(null);
          }}
          onClear={() => setSelected(null)}
          onRequestContribute={setContributingTerm}
        />
      </Card>

      {contributingTerm !== null ? (
        <Card gap="md">
          <SectionHeader title="Add to the index" />
          <ContributeTaxonForm
            initialName={contributingTerm}
            onCreated={(created) => {
              setSelected(created);
              setContributingTerm(null);
            }}
            onCancel={() => setContributingTerm(null)}
          />
        </Card>
      ) : null}

      {selected ? (
        <Card gap="md">
          <SectionHeader title={selected.scientificName} />
          <View style={stylesheet.badges}>
            <Badge tone="neutral">{RANK_LABELS[selected.rank]}</Badge>
            {selected.verified ? (
              <Badge tone="success">Verified</Badge>
            ) : (
              <Badge tone="warning">Community contributed</Badge>
            )}
          </View>

          <Stack gap="xs">
            {selected.commonName ? (
              <DetailRow label="Common name" value={selected.commonName} />
            ) : null}
            {selected.authorship ? (
              <DetailRow label="Authority" value={selected.authorship} />
            ) : null}
            {selected.familyName ? (
              <DetailRow label="Family" value={selected.familyName} />
            ) : null}
          </Stack>

          <SectionHeader title="Classification" />
          {lineage.data && lineage.data.length > 0 ? (
            <Stack gap="xs">
              {lineage.data.map((entry) => (
                <DetailRow
                  key={entry.id}
                  label={RANK_LABELS[entry.rank]}
                  value={entry.name}
                />
              ))}
            </Stack>
          ) : (
            <AppText size="xs">
              {lineage.isLoading
                ? "Loading classification..."
                : "No parent ranks recorded yet."}
            </AppText>
          )}

          <Button
            variant="outline"
            align="start"
            iconName="plus"
            onPress={() => setContributingTerm("")}
          >
            <ButtonText>Add a cultivar or variety under this</ButtonText>
          </Button>
        </Card>
      ) : null}

      <Card gap="sm">
        <SectionHeader
          title="Missing something?"
          description="Add it yourself. Contributions become part of the public index so the next person searching for it finds it too."
        />
        <Button
          variant="outline"
          align="start"
          iconName="plus"
          onPress={() => setContributingTerm("")}
        >
          <ButtonText>Add a plant name</ButtonText>
        </Button>
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  badges: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s },
});
